import * as R from 'ramda'

import { CurrencyCodesMap } from '../constants/localization/currency'
import i18n from '../locales/i18n'
import { Amount, AmountObj, Nil } from '../types'

export enum CurrencySymbolPositions {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

const getFormatter = (options = {} as Intl.NumberFormatOptions | undefined) =>
  new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: CurrencyCodesMap[i18n.language] || 'USD',
    ...options,
  })

export function isAmountObj(amount: Amount): amount is AmountObj {
  return R.is(Object, amount)
}

export const getAmountObjRange = ({ low, high }: AmountObj) =>
  `${getFormatter().format(low)}-${getFormatter().format(high)}`

export function formatMoney(
  amountProp: Amount | Nil,
  convertToNegative: boolean = false,
) {
  const amount = amountProp || 0
  if (isAmountObj(amount)) {
    return convertToNegative
      ? `(${getAmountObjRange(amount)})`
      : getAmountObjRange(amount)
  }
  const formatted = getFormatter()
    .format(convertToNegative && amount > 0 ? -amount : amount)
    .trim()
  return formatted.startsWith('-') ? `(${formatted.substring(1)})` : formatted
}

export const getCurrencySymbol = () =>
  R.pipe(
    getFormatter({ maximumFractionDigits: 0, minimumFractionDigits: 0 }).format,
    R.replace(/\d/g, ''),
    R.trim,
  )(0)

export const getDecimalSeparator = () =>
  getFormatter()
    .formatToParts(1.1)
    .find(part => part.type === 'decimal')?.value

export const getThousandSeparator = () =>
  getFormatter()
    .formatToParts(10000)
    .find(part => part.type === 'group')?.value

export function toPercentFormat(
  coefficient: number,
  maximumFractionDigits: number = 0,
) {
  return new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits,
  }).format(coefficient)
}

export const getCurrencySymbolPosition = () =>
  getFormatter({ currencyDisplay: 'code' })
    .formatToParts(0)
    .findIndex(
      part => part.value === (CurrencyCodesMap[i18n.language] || 'USD'),
    )
    ? CurrencySymbolPositions.AFTER
    : CurrencySymbolPositions.BEFORE

export const formatNumberWithDecimal = (amount: number) =>
  getFormatter({
    style: 'decimal',
    currency: undefined,
    currencySign: undefined,
  }).format(amount)
